import PropTypes from 'prop-types';
import React from 'react';
import { Mutation } from '@apollo/client/react/components';
import { Cell } from 'layout-core-components';

import { wrapKnownErrors } from './errors';
import {
  BULK_UPSERT_DATA_ENTRY_SEGMENTS,
  BULK_DELETE_DATA_ENTRY_SEGMENTS,
  GET_TRANSACTION_WITH_SEGMENTS,
} from './queries';
import SegmentInput from './SegmentInput';

/**
 * dataEntrySegments have a foreign key relation with clientCategories.
 * Use this relation to find any linked dataEntrySegments.
 * If none exist, return an object that can be used to create one.
 *
 * @param {Object} category A client segment category, see GET_CLIENT_SEGMENT_CATEGORIES for shape
 * @param {Object} dataEntrySegmentDetails See GET_DATA_ENTRY_SEGMENTS for shape
 *
 * @returns {{ amount: number, clientSegmentId: string, id: number }}
 */
export const getDataEntrySegmentForCategory = (category, dataEntrySegmentDetails) => {
  const dataEntrySegmentDetailsForCategory = dataEntrySegmentDetails.categories.find(
    cat => cat.clientCategoryId === category.id,
  );

  // The data model supports multiple segments per data entry
  // We do not, however, expose this ability to our users
  // So only use the first element of this array for UI purposes
  return dataEntrySegmentDetailsForCategory
    ? dataEntrySegmentDetailsForCategory.segments[0]
    : { clientSegmentId: null, id: null };
};

/**
 * Builds a change handler for creating or updating segments.
 * @param {func} updateFn - update mutation
 * @param {object} props
 * @param {array} dataEntryIds - ids of entries belonging to same ledger
 */
const buildChangeHandler =
  (bulkFn, { category, clientId }, dataEntryIds) =>
  async clientSegmentId => {
    try {
      await bulkFn({
        variables: {
          data: {
            categorySegmentSelections: [
              {
                categoryId: category.id,
                segmentId: clientSegmentId,
              },
            ],
            clientId,
            dataEntryIds,
          },
        },
      });
    } catch (err) {
      wrapKnownErrors(err);
    }
  };

/**
 * Builds a delete handler for removing segments.
 * @param {func} deleteFn - delete mutation
 * @param {object} props
 */
const buildDeleteHandler =
  (deleteFn, { clientId, ledgerAccountId, transactionId }) =>
  async clientSegmentId => {
    try {
      await deleteFn({
        variables: {
          clientId,
          journalEntryId: transactionId,
          ledgerId: ledgerAccountId,
          segmentId: clientSegmentId,
        },
      });
    } catch (err) {
      wrapKnownErrors(err);
    }
  };

/**
 * @param {Object.<string, any>} props Component props
 * @param {Object} props.category
 * @param {string} props.clientId
 * @param {Array} props.dataEntries
 * @param {Object} props.dataEntrySegmentDetails The segments a user has allocated for
 * this data entry. See GET_DATA_ENTRY_SEGMENTS for shape.
 *
 * @return {Node}
 */
export default function SegmentInputContainer(props) {
  const { category, dataEntryIds, dataEntrySegmentDetails, transactionId } = props;

  const refetchQueries = [
    {
      query: GET_TRANSACTION_WITH_SEGMENTS,
      variables: { transactionId },
    },
  ];

  const dataEntrySegment = getDataEntrySegmentForCategory(category, dataEntrySegmentDetails);

  const options = category.segments.map(({ name, id }) => ({
    label: name,
    value: id,
  }));

  return (
    <Mutation mutation={BULK_UPSERT_DATA_ENTRY_SEGMENTS} refetchQueries={refetchQueries}>
      {updateFn => (
        <Mutation mutation={BULK_DELETE_DATA_ENTRY_SEGMENTS} refetchQueries={refetchQueries}>
          {deleteFn => (
            <Cell size="auto">
              <SegmentInput
                categoryName={category.name}
                onChange={buildChangeHandler(updateFn, props, dataEntryIds)}
                onDelete={buildDeleteHandler(deleteFn, props)}
                options={options}
                selectedOption={options.find(o => o.value === dataEntrySegment.clientSegmentId)}
              />
            </Cell>
          )}
        </Mutation>
      )}
    </Mutation>
  );
}

SegmentInputContainer.propTypes = {
  category: PropTypes.object.isRequired, // eslint-disable-line
  dataEntryIds: PropTypes.arrayOf(PropTypes.string).isRequired,
  dataEntrySegmentDetails: PropTypes.object.isRequired, // eslint-disable-line
  transactionId: PropTypes.string.isRequired,
};
